import './components/calculators/format';
import './components/calculators/input';
import './components/calculators/products';

// =====================================================================
//  FIXED DEPOSIT — interactivity layer
//  Static markup lives in page-calculator-fixed-deposit.php.
// =====================================================================
(function () {
  const fmt = Calc.format;

  // FD-specific math — kept local (finance.js is for SHARED formulas only).
  // Malaysian FD pays SIMPLE interest at maturity (not compounded):
  //   interest = P × (rate%/100) × (months / 12);  maturity = P + interest.
  // annualRate is a percent (e.g. 3.5); months is the tenure in months.
  // monthlyInterest is the informational per-month equivalent (interest / months).
  function fixedDeposit(principal, annualRate, months) {
    const p = principal || 0;
    const m = months || 0;
    if (p <= 0 || m <= 0) return { interest: 0, maturity: p, monthlyInterest: 0 };
    const interest = p * ((annualRate || 0) / 100) * (m / 12);
    return { interest, maturity: p + interest, monthlyInterest: interest / m };
  }

  // Reference-grid axes. Amounts are rows (RM). The tenure columns are read from
  // the table's own <th data-tenure> so the axis can never drift out of sync with
  // the rendered headers — add a column in the template and JS picks it up.
  const REF_AMOUNTS = [1000, 5000, 10000, 20000, 30000, 50000, 100000, 200000];
  const refTenures = () =>
    [...document.querySelectorAll('.ref-grid thead th[data-tenure]')].map(th => +th.dataset.tenure);

  const state = {
    amount: 10000,
    rate: 3.5,        // percent p.a. (e.g. 3.5)
    tenure: 12,       // months
    type: 'conventional',
    activeTab: 'breakdown',
    sort: { key: 'best', dir: 'desc' },   // template's selected option sets the default
  };

  // Unit word ("months") is authored in the HTML — read it, never hard-code copy.
  let UNIT_MONTHS = 'months';

  const setText = (id, val) => {
    const el = document.getElementById(id);
    if (el) el.textContent = val;
  };
  const setVal = (sel, val) => {
    const el = document.querySelector(sel);
    if (el) el.textContent = val;
  };
  const toggleAll = (sel, hide) => {
    document.querySelectorAll(sel).forEach(el => el.classList.toggle('d-none', hide));
  };
  const syncInput = (id, val) => Calc.input.syncValue(id, val);
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  function updateStatBand() {
    const fd = fixedDeposit(state.amount, state.rate, state.tenure);
    setText('fd-stat-monthly',  fmt.money(fd.monthlyInterest, 0));
    setText('fd-stat-rate',     fmt.percent(state.rate / 100));
    setText('fd-stat-interest', fmt.money(fd.interest, 0));
    setText('fd-stat-maturity', fmt.money(fd.maturity, 0));
  }

  function updateBreakdown() {
    const fd = fixedDeposit(state.amount, state.rate, state.tenure);
    setText('fd-r-amount',   fmt.money(state.amount));
    setVal('.fd-r-tenure-m', state.tenure);
    setVal('.fd-r-tenure-y', (state.tenure / 12).toFixed(1));
    setVal('.fd-r-rate-num', fmt.percent(state.rate / 100));
    setText('fd-r-interest', fmt.money(fd.interest));
    setText('fd-r-maturity', fmt.money(fd.maturity));
    setText('fd-r-monthly',  fmt.money(fd.monthlyInterest));
  }

  function updateReferenceGrid() {
    setVal('.fd-grid-rate-num', fmt.percent(state.rate / 100));
    const tbody = document.getElementById('fd-grid-tbody');
    if (!tbody) return;
    const tenures = refTenures();
    tbody.innerHTML = REF_AMOUNTS.map(amt => {
      const isHighRow = amt === state.amount;
      const cells = tenures.map(t => {
        const interest = fixedDeposit(amt, state.rate, t).interest;
        const isHighCol = t === state.tenure;
        return `<td class="${isHighCol ? 'highlight-col' : ''}">${fmt.number(interest, 0)}</td>`;
      }).join('');
      return `<tr class="${isHighRow ? 'highlight' : ''}"><th>RM ${amt.toLocaleString()}</th>${cells}</tr>`;
    }).join('');
  }

  function updateBankCards() {
    const wraps = document.querySelectorAll('.bank-card-wrap');
    const emptyState = document.querySelector('#fd-bank-grid .bank-empty-state');
    const note = document.getElementById('fd-ineligible-note');

    // Reason wording templates come from the HTML (values only are substituted).
    const tplMinDeposit = note?.dataset.reasonMinDeposit || '';
    const tplMaxDeposit = note?.dataset.reasonMaxDeposit || '';
    const tplTenure     = note?.dataset.reasonTenure || '';

    const eligible = [];
    const ineligibleReasons = [];

    wraps.forEach((wrap, i) => {
      // Conventional/Islamic is a product-category filter, not an eligibility
      // failure — a non-matching bank is hidden outright rather than listed in
      // the ineligible panel with a reason.
      if (wrap.dataset.type && wrap.dataset.type !== state.type) {
        wrap.classList.add('d-none');
        return;
      }

      // The feed carries ONE advertised rate per product (percent, e.g. 2.35)
      // plus its deposit and tenure bounds — there is no per-tenure board-rate
      // table, so that rate applies across the product's whole tenure range.
      const rate = +wrap.dataset.rate;
      if (!rate) { wrap.classList.add('d-none'); return; }   // no usable rate — not an eligibility failure

      const minAmt = +wrap.dataset.minAmount || 0;
      const maxAmt = +wrap.dataset.maxAmount || Infinity;
      const minTen = +wrap.dataset.minTenure || 0;
      const maxTen = +wrap.dataset.maxTenure || Infinity;

      const reasons = [];
      if (state.amount < minAmt) {
        reasons.push(tplMinDeposit.replace('{min}', fmt.number(minAmt, 0)));
      }
      if (state.amount > maxAmt) {
        reasons.push(tplMaxDeposit.replace('{max}', fmt.number(maxAmt, 0)));
      }
      if (state.tenure < minTen || state.tenure > maxTen) {
        reasons.push(tplTenure.replace('{min}', minTen).replace('{max}', maxTen));
      }
      const isEligible = reasons.length === 0;

      wrap.classList.toggle('d-none', !isEligible);
      if (!isEligible) {
        const name = wrap.querySelector('.bank-apply')?.dataset.value || `#${wrap.dataset.bankId}`;
        ineligibleReasons.push(`<strong>${esc(name)}:</strong> ${esc(reasons.join(' '))}`);
        return;
      }

      const fd = fixedDeposit(state.amount, rate, state.tenure);

      const rateEl = wrap.querySelector('.bank-rate');
      if (rateEl) rateEl.textContent = fmt.percent(rate / 100);
      const tenEl = wrap.querySelector('.bank-tenure');
      if (tenEl) tenEl.textContent = `${state.tenure} ${UNIT_MONTHS}`;
      const intEl = wrap.querySelector('.bank-interest');
      if (intEl) intEl.textContent = fmt.money(fd.interest, 0);
      const matEl = wrap.querySelector('.bank-maturity');
      if (matEl) matEl.textContent = fmt.money(fd.maturity, 0);

      const apply = wrap.querySelector('.bank-apply');
      const base  = wrap.dataset.applyUrl;
      if (apply && base) {
        try {
          const url = new URL(base);
          url.searchParams.set('amount', state.amount);
          url.searchParams.set('tenure', state.tenure);
          apply.href = url.toString();
        } catch (e) { apply.href = base; }
      }

      // `best` = the advertised rate (higher is better; drives the badge).
      eligible.push({
        wrap,
        index: i,
        name: wrap.querySelector('.bank-apply')?.dataset.value || '',
        // Only axes that actually reorder the visible cards (see products.js).
        metrics: { best: rate },
      });
    });

    // Best = highest advertised rate (⇒ highest interest/maturity at any tenure).
    // Badge = best value; order = the user's chosen sort.
    const bestWrap = eligible.length
      ? Calc.products.rank(eligible, 'best', 'desc')[0].wrap
      : null;
    Calc.products.applyOrder(
      wraps,
      Calc.products.rank(eligible, state.sort.key, state.sort.dir),
      bestWrap
    );

    if (emptyState) emptyState.classList.toggle('d-none', eligible.length > 0);

    const reasonsEl = document.getElementById('fd-ineligible-reasons');
    if (note && reasonsEl) {
      if (ineligibleReasons.length) {
        note.classList.remove('d-none');
        reasonsEl.innerHTML = ineligibleReasons.join('\n');
      } else {
        note.classList.add('d-none');
      }
    }
  }

  function refresh() {
    syncInput('fd-amount-input', state.amount);
    syncInput('fd-rate-input',   state.rate.toFixed(2));
    syncInput('fd-tenure-input', state.tenure);

    // Conventional / Islamic wording variants — toggle the HTML-rendered spans.
    const isIsl = state.type === 'islamic';
    toggleAll('.fd-word-conv',       isIsl);
    toggleAll('.fd-word-isl',       !isIsl);
    toggleAll('.fd-grid-word-conv',  isIsl);
    toggleAll('.fd-grid-word-isl',  !isIsl);
    document.querySelectorAll('#fd-type-tabs button').forEach(btn => {
      const active = btn.dataset.type === state.type;
      btn.classList.toggle('btn-primary', active);
      btn.classList.toggle('btn-outline-primary', !active);
    });

    updateStatBand();
    updateBankCards();

    if (state.activeTab === 'breakdown') updateBreakdown();
    else updateReferenceGrid();
  }

  function activateTab(tabId) {
    state.activeTab = tabId;
    document.querySelectorAll('#fd-tabs .btn-tab').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tabId);
    });
    document.querySelectorAll('.tab-panel').forEach(panel => {
      panel.classList.toggle('d-none', panel.id !== `fd-tab-${tabId}`);
    });
    if (tabId === 'breakdown') updateBreakdown();
    else updateReferenceGrid();
  }

  function bindEvents() {
    ['input', 'change'].forEach(evt => document.addEventListener(evt, (e) => {
      const id = e.target.id;
      const v = Calc.input.getValue(e.target);
      if (id === 'fd-amount-input') { state.amount = v || 0; refresh(); }
      if (id === 'fd-rate-input')   { state.rate   = v || 0; refresh(); }
      if (id === 'fd-tenure-input') { state.tenure = v || 1; refresh(); }
    }));

    document.querySelectorAll('#fd-tabs .btn-tab').forEach(btn => {
      btn.addEventListener('click', () => activateTab(btn.dataset.tab));
    });

    document.querySelectorAll('#fd-type-tabs button').forEach(btn => {
      btn.addEventListener('click', () => { state.type = btn.dataset.type; refresh(); });
    });

    const initialSort = Calc.products.bindSort('#fd-sort-select', (sort) => {
      state.sort = sort;
      updateBankCards();
    });
    if (initialSort) state.sort = initialSort;
  }

  document.addEventListener('DOMContentLoaded', () => {
    const unitEl = document.querySelector('#fd-tenure-input')?.closest('.input-group')?.querySelector('.input-group-text');
    if (unitEl && unitEl.textContent.trim()) UNIT_MONTHS = unitEl.textContent.trim();
    bindEvents();
    refresh();
  });
})();
